import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import type { ReactNode } from "react";
import superjson from "superjson";
import { getLoginUrl, USE_LOCAL_AUTH } from "./const";

function makeQueryClient(ssr = false) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: ssr ? Infinity : 0,
      },
    },
  });

  if (ssr) return queryClient;

  const redirectToLoginIfUnauthorized = (error: unknown) => {
    if (!(error instanceof TRPCClientError)) return;
    if (typeof window === "undefined") return;
    if (error.message !== UNAUTHED_ERR_MSG) return;
    // MANUS OAUTH redirect — skipped while USE_LOCAL_AUTH is true
    if (USE_LOCAL_AUTH) return;
    const loginUrl = getLoginUrl();
    if (loginUrl) window.location.href = loginUrl;
  };

  queryClient.getQueryCache().subscribe(event => {
    if (event.type === "updated" && event.action.type === "error") {
      redirectToLoginIfUnauthorized(event.query.state.error);
    }
  });

  queryClient.getMutationCache().subscribe(event => {
    if (event.type === "updated" && event.action.type === "error") {
      redirectToLoginIfUnauthorized(event.mutation.state.error);
    }
  });

  return queryClient;
}

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

let browserQueryClient: QueryClient | undefined;

function getQueryClient(ssr = false) {
  if (ssr) return makeQueryClient(true);
  if (!browserQueryClient) browserQueryClient = makeQueryClient(false);
  return browserQueryClient;
}

export function AppProviders({
  children,
  ssr = false,
}: {
  children: ReactNode;
  ssr?: boolean;
}) {
  const queryClient = getQueryClient(ssr);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
